import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authToken } from '../../api/apiBase';
import { PHASES_RETAINED_READ_TIMEOUT_MS } from '../../lib/mackPhasesEvidence';
import {
  PhasesUsdCpiAdoptionStatus, PhasesUsdCpiAdoptionTimeoutError, readUsdCpiAdoption,
  submitUsdCpiAdoption, usdCpiAdoptionFailure, usdCpiAdoptionOwnerAuthorized,
} from '../../lib/mackPhasesUsdCpiAdoption';
import { PhasesEvidenceSurface, panelNote, panelTitle } from './primitives';
import { C, MONO, SANS } from './tokens';

const ongoing = new Set(['QUEUED', 'RUNNING', 'RECOVERING', 'PERSISTENCE_PENDING']);
const readable = (state: string) => state.replace(/_/g, ' ').toLowerCase();
interface Flight { controller: AbortController; deadline: number }
interface Session { token: string; active: boolean; flight?: Flight }
interface Snapshot { token: string; status: PhasesUsdCpiAdoptionStatus }
const text = (value: unknown) => value === null || value === undefined || value === '' ? 'Not projected' : String(value);

function ReceiptPeriods({ label, periods }: { label: string; periods?: string[] }) {
  return periods ? <details>
    <summary>{label}: {periods.length} distinct {periods.length === 1 ? 'period' : 'periods'}</summary>
    <p>{periods.join(', ') || 'No periods recorded.'}</p>
  </details> : <p>{label}: period identities have not been projected from the adoption receipt.</p>;
}

export function PhasesUsdCpiHistoryAdoption({ token }: { token?: string | null }) {
  const effectiveToken = token || authToken();
  const session = useRef<Session | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [unconfirmedSubmission, setUnconfirmedSubmission] = useState(false);
  const status = snapshot?.token === effectiveToken ? snapshot.status : null;

  const request = useCallback(async (submit: boolean) => {
    const current = session.current;
    if (!current || !current.active || current.token !== effectiveToken || current.flight) return;
    const controller = new AbortController();
    const isCurrent = () => current.active && session.current === current && current.flight === flight;
    const finish = () => {
      window.clearTimeout(flight.deadline);
      if (isCurrent()) { current.flight = undefined; setBusy(false); setSubmitting(false); }
    };
    const flight: Flight = { controller, deadline: window.setTimeout(() => {
      if (!isCurrent()) return;
      setError(usdCpiAdoptionFailure(new PhasesUsdCpiAdoptionTimeoutError(), submit));
      finish(); controller.abort();
    }, PHASES_RETAINED_READ_TIMEOUT_MS) };
    current.flight = flight;
    if (submit) setUnconfirmedSubmission(true);
    setBusy(true); setSubmitting(submit); setError('');
    try {
      const next = await (submit ? submitUsdCpiAdoption : readUsdCpiAdoption)(current.token, controller.signal);
      if (isCurrent()) { setSnapshot({ token: current.token, status: next }); setUnconfirmedSubmission(false); }
    } catch (cause) {
      if (isCurrent()) setError(usdCpiAdoptionFailure(cause, submit));
    } finally { finish(); }
  }, [effectiveToken]);

  useEffect(() => {
    const current: Session = { token: effectiveToken, active: true };
    session.current = current; setSnapshot(null); setError(''); setUnconfirmedSubmission(false);
    void request(false);
    return () => {
      current.active = false;
      if (current.flight) { window.clearTimeout(current.flight.deadline); current.flight.controller.abort(); }
    };
  }, [effectiveToken, request]);

  const operation = status?.operation;
  useEffect(() => {
    if (busy || error || !operation || !ongoing.has(operation.state)) return;
    const timer = window.setTimeout(() => { void request(false); }, 4000);
    return () => window.clearTimeout(timer);
  }, [busy, error, operation, request, snapshot]);

  const ownerAuthorized = status ? usdCpiAdoptionOwnerAuthorized(status) : false;
  const canSubmit = !busy && !error && !unconfirmedSubmission && status?.can_submit === true && !operation && ownerAuthorized;
  const lastConfirmed = !!status && (busy || !!error || unconfirmedSubmission);
  const inventory = status?.inventory_summary;
  const buttonStyle: React.CSSProperties = { border: `1px solid ${C.chipOffBd}`, borderRadius: 8,
    background: C.chipOffBg, color: C.text, padding: '8px 12px', fontFamily: SANS, fontSize: 13, cursor: 'pointer' };

  return <PhasesEvidenceSurface>
    <h2 style={{ ...panelTitle, margin: 0 }}>Retained USD CPI history</h2>
    <p style={panelNote}>Adopt the sealed, already retained published headline CPI-U annual-change history through the resident writer. This is historical evidence, not a new current-release acquisition.</p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <button type="button" disabled={!canSubmit} style={{ ...buttonStyle, color: canSubmit ? C.amberBright : C.textMuted,
        cursor: canSubmit ? 'pointer' : 'default' }} onClick={() => { if (canSubmit) void request(true); }}>
        {submitting ? 'Submitting retained-history adoption…' : 'Adopt retained USD CPI history'}
      </button>
      <button type="button" disabled={busy} style={buttonStyle} onClick={() => { void request(false); }}>Refresh USD CPI adoption status</button>
    </div>
    <div aria-live="polite" style={panelNote}>
      {busy && !submitting && <p>Reading retained USD CPI adoption status…</p>}
      {error && <p role="alert">{error}</p>}
      {lastConfirmed && <p>Showing the last confirmed adoption status. The current request has not established a newer outcome.</p>}
      {status && <>
        <p>{ownerAuthorized ? `${lastConfirmed ? 'Last confirmed: ' : ''}Owner retained-history adoption permission verified.` : 'Owner retained-history adoption permission is not verified.'}</p>
        <p>Installed adoption authority: {readable(status.authority_state)}. Resident mechanism: {status.resident_available ? 'available' : 'unavailable'}.</p>
        {status.blocker && <p>Adoption withheld: {status.blocker}</p>}
        {inventory ? <>
          <p>Sealed inventory: {inventory.total} published periods · {inventory.first_period}–{inventory.last_period}. Current missing periods in this sealed scope: {inventory.missing}.</p>
          <details><summary>Sealed archive periods already retained in Inputs or Phases: {inventory.overlap_periods.length} distinct {inventory.overlap_periods.length === 1 ? 'period' : 'periods'}</summary>
            <p>{inventory.overlap_periods.join(', ') || 'No retained overlap projected.'}</p>
            <p>Coverage is limited to the sealed archive period list. The adoption receipt below identifies its Inputs reuse and Phases admissions.</p>
          </details>
          <p>Unpublished periods excluded: {inventory.unpublished_periods.join(', ') || 'None projected'}.</p>
          <details><summary>Sealed inventory identity</summary><p style={{ fontFamily: MONO, overflowWrap: 'anywhere' }}>{inventory.sealed_sha256}</p></details>
        </> : <p>Sealed retained inventory is unavailable; adoption remains withheld.</p>}
        {!operation && <p>{unconfirmedSubmission ? 'An adoption acknowledgement is unconfirmed. Refresh adoption status; do not submit again.'
          : `${lastConfirmed ? 'Last confirmed: ' : ''}No retained-history adoption request submitted. No adoption has started.`}</p>}
        {operation && <>
          <p>{lastConfirmed ? 'Last confirmed: ' : ''}Retained-history adoption: {readable(operation.state)}.</p>
          {operation.state === 'QUEUED' && <p>Request accepted and queued. Historical observations have not yet been acknowledged as persisted.</p>}
          {operation.state === 'RUNNING' && <p>The resident worker is validating and persisting the retained history.</p>}
          {['RECOVERING', 'PERSISTENCE_PENDING'].includes(operation.state) && <p>Persistence recovery is pending. Inspect the durable transaction and intent; do not submit another request.</p>}
          {operation.state === 'COMPLETED' && <p>Terminal adoption receipt available. Required history coverage and persistence must be assessed from the retained receipts.</p>}
          {['PARTIAL', 'FAILED', 'WITHHELD'].includes(operation.state) && <p>The adoption has a partial, failed or withheld outcome. Inspect the receipt; no automatic resubmission is offered.</p>}
          <p style={{ fontFamily: MONO, overflowWrap: 'anywhere' }}>Adoption receipt: {operation.request_id}</p>
          <p>Requested: {operation.requested_at} · Started: {text(operation.started_at)} · Ended: {text(operation.ended_at)}</p>
          <p>Writer adoption time: {text(operation.adoption_at)}. Original source retrieval clocks remain separate in the retained response references below.</p>
          <p>New publisher requests: {operation.source_requests}.</p>
          {['returned', 'admitted', 'deduplicated', 'revised'].some(key => operation[key] !== undefined)
            ? <p>{['returned', 'admitted', 'deduplicated', 'revised'].filter(key => operation[key] !== undefined)
              .map(key => `${readable(key)}: ${operation[key]}`).join(' · ')}</p>
            : <p>Admission, deduplication and revision counts have not yet been projected from durable writer receipts.</p>}
          <p>Required persistence: {operation.persistence_complete === true ? 'complete' : operation.persistence_complete === false ? 'incomplete' : 'not yet proven'}.</p>
          <ReceiptPeriods label="Inputs periods reused read-only by this adoption"
            periods={operation.upstream_periods_reused_no_copy?.map(row => row.reference_period)} />
          <ReceiptPeriods label="Phases-native periods admitted by this adoption" periods={operation.admitted_periods} />
          <p style={{ overflowWrap: 'anywhere' }}>Original recovery: {operation.original_operation_id} · Original USD CPI claim: {operation.original_claim_identity}</p>
          <p style={{ overflowWrap: 'anywhere' }}>Receipt location: {operation.receipt_location}</p>
        </>}
        <details><summary>Sealed inventory, original retrieval clocks and complete adoption status</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 11 }}>{JSON.stringify(status, null, 2)}</pre>
        </details>
      </>}
    </div>
  </PhasesEvidenceSurface>;
}
